import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMarketplace, useMasterData } from 'hooks';
import { PetType } from '@spike/model';
import { FixedPriceOption, LateCheckoutFeeType } from 'model/Service';
import styles from './BoardingFixedPriceTable.module.scss';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { MarketplaceDepositType } from '@spike/marketplace-model';
import { AlertMessage } from 'components/UI/V3/AlertMessage';
import PriceField from 'components/UI/V3/Forms/PriceField';

interface BoardingFixedPriceTableProps {
    selectedPetTypes: Array<PetType>;
    fixedPrices: FixedPriceOption[];
    onFixedPricesChange: (prices: FixedPriceOption[]) => void;
    showLateCheckoutColumn?: boolean;
    lateCheckoutFeeType?: LateCheckoutFeeType;
    lateCheckoutFees?: Record<string, number>; // petTypeId -> late checkout fee amount
    onLateCheckoutFeesChange?: (fees: Record<string, number>) => void;
    className?: string;
    onValidationChange?: (isValid: boolean, errors: Record<string, string>) => void;
}

export const BoardingFixedPriceTable: React.FC<BoardingFixedPriceTableProps> = ({
    selectedPetTypes,
    fixedPrices,
    onFixedPricesChange,
    showLateCheckoutColumn = false,
    lateCheckoutFeeType,
    lateCheckoutFees = {},
    onLateCheckoutFeesChange,
    className,
    onValidationChange
}) => {
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const deposit = marketplace.deposit;
    const depositType = deposit.type;

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [validatedFields, setValidatedFields] = useState<Set<string>>(new Set());

    const availablePetTypes = useMemo(() => {
        return masterData.petTypes.filter(petType => selectedPetTypes.some(selectedPt => selectedPt.id === petType.id));
    }, [selectedPetTypes, masterData.petTypes]);

    useEffect(() => {
        if (availablePetTypes.length === 0) return;

        const existingPetTypeIds = new Set(fixedPrices.map(price => price.petType.id));
        const missingPetTypes = availablePetTypes.filter(petType => !existingPetTypeIds.has(petType.id));
        const currentPetTypeIds = new Set(availablePetTypes.map(pt => pt.id));
        const validPrices = fixedPrices.filter(price => currentPetTypeIds.has(price.petType.id));

        if (missingPetTypes.length > 0 || validPrices.length !== fixedPrices.length) {
            const newPrices = missingPetTypes.map(petType => ({
                petType,
                price: 0
            }));
            const updatedPrices = [...validPrices, ...newPrices];
            onFixedPricesChange(updatedPrices);
        }
    }, [availablePetTypes, fixedPrices, onFixedPricesChange]);

    const handlePriceChange = (petTypeId: string, value: number | undefined) => {
        const price = value || 0;
        const updatedPrices = fixedPrices.map(priceOption => {
            if (priceOption.petType.id === petTypeId) {
                return { ...priceOption, price };
            }
            return priceOption;
        });
        onFixedPricesChange(updatedPrices);
    };

    const getCurrentPrice = (petTypeId: string): number | undefined => {
        const priceOption = fixedPrices.find(p => p.petType.id === petTypeId);
        return priceOption?.price !== undefined && priceOption?.price !== null && priceOption.price >= 0
            ? Number(priceOption.price.toFixed(2))
            : undefined;
    };

    const handleLateCheckoutFeeChange = (petTypeId: string, value: number | undefined) => {
        if (!onLateCheckoutFeesChange) return;

        const fee = value || 0;
        const updatedFees = {
            ...lateCheckoutFees,
            [petTypeId]: fee
        };
        onLateCheckoutFeesChange(updatedFees);
    };

    const getCurrentLateCheckoutFee = (petTypeId: string): number | undefined => {
        const fee = lateCheckoutFees[petTypeId];
        return fee && fee > 0 ? fee : undefined;
    };

    // Helper to get the header of the Late Checkout column
    const getLateCheckoutColumnHeader = (): string => {
        if (!lateCheckoutFeeType) return 'Late Checkout Fee';

        switch (lateCheckoutFeeType) {
            case 'fixed_fee':
                return 'Fixed Fee';
            case 'per_hour':
                return 'Per Hour';
            case 'additional_night':
                return 'Additional Night';
            default:
                return 'Late Checkout Fee';
        }
    };

    // Helper to get the description of the Late Checkout Fee
    const getLateCheckoutDescription = (): string => {
        if (!lateCheckoutFeeType) return '';

        switch (lateCheckoutFeeType) {
            case 'fixed_fee':
                return 'Set a fixed fee if a pet checks out late.';
            case 'per_hour':
                return 'Set an hourly rate if a pet checks out late.';
            case 'additional_night':
                return 'Set an additional night rate if a pet checks out late.';
            default:
                return '';
        }
    };

    const isPriceLowerThanDeposit = (petTypeId: string): boolean => {
        if (depositType !== MarketplaceDepositType.Amount) {
            return false;
        }
        const currentPrice = getCurrentPrice(petTypeId);
        if (!currentPrice || currentPrice <= 0) {
            return false;
        }
        const depositAmount = typeof deposit.value === 'number' ? deposit.value : parseFloat(deposit.value || '0');
        return currentPrice < depositAmount;
    };

    const handlePriceBlur = useCallback(
        (petTypeId: string) => {
            const newValidatedFields = new Set(validatedFields);
            newValidatedFields.add(petTypeId);
            setValidatedFields(newValidatedFields);

            const errors = { ...validationErrors };
            if (isPriceLowerThanDeposit(petTypeId)) {
                errors[petTypeId] = 'Price is lower than deposit amount';
            } else {
                delete errors[petTypeId];
            }
            setValidationErrors(errors);
            onValidationChange?.(Object.keys(errors).length === 0, errors);
        },
        [validatedFields, validationErrors, onValidationChange, isPriceLowerThanDeposit]
    );

    const shouldShowDepositAlert = (petTypeId: string): boolean => {
        return validatedFields.has(petTypeId) && isPriceLowerThanDeposit(petTypeId);
    };

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    <span className={styles.typeColumn}>Type</span>
                    <span className={styles.priceColumn}>
                        Price <span className={styles.required}>*</span>
                    </span>
                    {showLateCheckoutColumn && (
                        <span className={styles.lateCheckoutColumn}>
                            {getLateCheckoutColumnHeader()} <span className={styles.required}>*</span>
                            <Tooltip
                                content={getLateCheckoutDescription()}
                                placement="top"
                                className={styles.customTooltip}
                            >
                                <div>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className={styles.tooltipIcon}
                                    />
                                </div>
                            </Tooltip>
                        </span>
                    )}
                </div>
            </div>

            <div className={styles.rows}>
                {availablePetTypes.map(petType => (
                    <React.Fragment key={petType.id}>
                        <div className={styles.row}>
                            <div className={styles.typeCell}>
                                <span className={styles.petTypeName}>{petType.plural}</span>
                            </div>
                            <div className={styles.priceCell}>
                                <div className={styles.priceInputContainer}>
                                    <PriceField
                                        scale={2}
                                        value={getCurrentPrice(petType.id)}
                                        onChange={(value: number | undefined) => handlePriceChange(petType.id, value)}
                                        onBlur={() => handlePriceBlur(petType.id)}
                                        placeholder="0.00"
                                        className={styles.priceInput}
                                    />
                                    <span className={styles.priceUnit}>per night</span>
                                </div>
                            </div>
                            {showLateCheckoutColumn && (
                                <div className={styles.lateCheckoutCell}>
                                    <div className={styles.priceInputContainer}>
                                        <PriceField
                                            scale={2}
                                            value={getCurrentLateCheckoutFee(petType.id)}
                                            onChange={(value: number | undefined) =>
                                                handleLateCheckoutFeeChange(petType.id, value)
                                            }
                                            placeholder="0.00"
                                            className={styles.priceInput}
                                        />
                                    </div>
                                </div>
                            )}
                            {shouldShowDepositAlert(petType.id) && (
                                <AlertMessage
                                    icon={faInfoCircle}
                                    iconColor="#BAA997"
                                    bdColor="#BCB8AE"
                                    bgColor="#F8F5F1"
                                    className={styles.alert}
                                >
                                    <span>
                                        The price entered is lower than the price you have set for your deposits.
                                    </span>
                                </AlertMessage>
                            )}
                        </div>
                    </React.Fragment>
                ))}
            </div>

            {availablePetTypes.length === 0 && (
                <div className={styles.emptyState}>
                    <p>Please select pet types in Step 1 to configure pricing</p>
                </div>
            )}
        </div>
    );
};
